import React, { useState, useEffect } from 'react';
import { MdAssessment, MdRefresh, MdPeople, MdRoute, MdFileDownload, MdDirectionsCar, MdSecurity, MdCompareArrows, MdEventBusy, MdFactCheck } from 'react-icons/md';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement,
  Title, Tooltip, Legend, Filler
} from 'chart.js';
import {
  getEmployeeTravelReport, getScanLogReport, getAccessibleLocations,
  getNoShowReport, getAttendanceReport, getVehicleUtilization, getGatemanReport, getMonthComparison,
  getLocationSummary
} from '../../../api/vehicleAnalytics';
import * as XLSX from 'xlsx';
import '../../../styles/VehicleScanReports.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

function VehicleScanReports() {
  const [activeTab, setActiveTab] = useState('overview');
  const [reportData, setReportData] = useState(null);
  const [scanLogs, setScanLogs] = useState([]);
  const [noShowData, setNoShowData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [vehicleUtilData, setVehicleUtilData] = useState(null);
  const [gatemanData, setGatemanData] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [locationSummaryData, setLocationSummaryData] = useState(null);
  const [expandedDept, setExpandedDept] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState('');
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    location: '', timing: '', route: '', empId: '', vehicle: '', scannedBy: '', empSearchTerm: ''
  });
  const [empSort, setEmpSort] = useState({ key: 'totalTrips', direction: 'desc' });
  const [deptSort, setDeptSort] = useState({ key: 'totalScans', direction: 'desc' });
  const [attSort, setAttSort] = useState({ key: 'complianceRate', direction: 'asc' });
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  useEffect(() => {
    getAccessibleLocations().then(res => {
      if (res?.data && Array.isArray(res.data) && res.data.length > 0) setLocations(res.data);
    }).catch(() => {});
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true); setError('');
      const response = await getEmployeeTravelReport(filters.startDate, filters.endDate, filters.location || undefined);
      if (response?.data) setReportData(response.data);
      else throw new Error('Failed to load report');
    } catch (err) { setError('Failed to load report.'); }
    finally { setLoading(false); }
  };

  const dedupeScanLogs = (logs) => {
    const seen = new Map();
    for (const s of logs) {
      const key = `${s.empId}|${s.scanTime}|${s.travelingVehicle}`;
      if (!seen.has(key)) seen.set(key, s);
    }
    return Array.from(seen.values());
  };

  const searchScanLogs = async (currentFilters) => {
    setTabLoading(true);
    try {
      const r = await getScanLogReport(currentFilters);
      setScanLogs(dedupeScanLogs(r?.data || []));
    } catch (err) { console.error('Scan log search error:', err); setScanLogs([]); }
    finally { setTabLoading(false); }
  };

  const loadTabData = async (tab, force = false) => {
    setTabLoading(true);
    try {
      const loc = filters.location || undefined;
      if (tab === 'scanLogs' && (force || scanLogs.length === 0)) {
        const r = await getScanLogReport(filters);
        if (r?.data) setScanLogs(dedupeScanLogs(r.data));
      } else if (tab === 'noShow' && (force || !noShowData)) {
        const r = await getNoShowReport(filters.startDate, filters.endDate, loc);
        if (r?.data) setNoShowData(r.data);
      } else if (tab === 'attendance' && (force || !attendanceData)) {
        const r = await getAttendanceReport(filters.startDate, filters.endDate, loc);
        if (r?.data) setAttendanceData(r.data);
      } else if (tab === 'vehicleUtil' && (force || !vehicleUtilData)) {
        const r = await getVehicleUtilization(filters.startDate, filters.endDate, loc);
        if (r?.data) setVehicleUtilData(r.data);
      } else if (tab === 'gateman' && (force || !gatemanData)) {
        const r = await getGatemanReport(filters.startDate, filters.endDate, loc);
        if (r?.data) setGatemanData(r.data);
      } else if (tab === 'monthCompare' && (force || !monthData)) {
        const r = await getMonthComparison(loc);
        if (r?.data) setMonthData(r.data);
      } else if (tab === 'locationSummary' && (force || !locationSummaryData)) {
        const r = await getLocationSummary(filters.startDate, filters.endDate);
        if (r?.data) setLocationSummaryData(r.data);
      }
    } catch (err) { console.error('Tab load error:', err); }
    finally { setTabLoading(false); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    loadTabData(tab);
  };

  const handleFilterApply = () => {
    // Reset cached data on filter change
    setNoShowData(null); setAttendanceData(null); setVehicleUtilData(null);
    setGatemanData(null); setMonthData(null); setScanLogs([]);
    setLocationSummaryData(null);
    loadReport();
    if (activeTab !== 'overview' && activeTab !== 'department' && activeTab !== 'employee' && activeTab !== 'route') {
      // force = true bypasses stale cache check — always reloads with latest filters
      setTimeout(() => loadTabData(activeTab, true), 100);
    }
  };

  const sortData = (data, sortConfig) => {
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key]; const bVal = b[sortConfig.key];
      if (aVal == null) return 1; if (bVal == null) return -1;
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleEmpSort = (key) => setEmpSort(p => ({ key, direction: p.key === key && p.direction === 'desc' ? 'asc' : 'desc' }));
  const handleDeptSort = (key) => setDeptSort(p => ({ key, direction: p.key === key && p.direction === 'desc' ? 'asc' : 'desc' }));
  const handleAttSort = (key) => setAttSort(p => ({ key, direction: p.key === key && p.direction === 'desc' ? 'asc' : 'desc' }));
  const getSortIcon = (sc, key) => sc.key !== key ? '↕' : sc.direction === 'asc' ? '↑' : '↓';

  const exportToExcel = (data, name) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, name);
    XLSX.writeFile(wb, `${name}_${filters.startDate}_to_${filters.endDate}.xlsx`);
  };

  const exportDepartmentReport = () => { if (!reportData?.departmentStats) return; exportToExcel(reportData.departmentStats.map(d => ({ 'Department': d.department, 'Employees': d.employeeCount, 'Total Scans': d.totalScans, 'Morning': d.morningScans, 'Evening': d.eveningScans, 'Avg/Employee': d.avgScansPerEmployee, 'Share %': d.percentage })), 'Department_Report'); };
  const exportEmployeeReport = () => {
    if (!reportData?.employeeTravelSummary) return;
    const deduped = Array.from(
      reportData.employeeTravelSummary.reduce((map, e) => {
        const key = e.empId || `${e.employeeName}|${e.route}`;
        if (!map.has(key)) { map.set(key, e); }
        else {
          const prev = map.get(key);
          map.set(key, { ...prev, totalTrips: (prev.totalTrips || 0) + (e.totalTrips || 0), morningTrips: (prev.morningTrips || 0) + (e.morningTrips || 0), eveningTrips: (prev.eveningTrips || 0) + (e.eveningTrips || 0), mismatchCount: (prev.mismatchCount || 0) + (e.mismatchCount || 0), lastScanDate: (prev.lastScanDate || '') > (e.lastScanDate || '') ? prev.lastScanDate : e.lastScanDate });
        }
        return map;
      }, new Map()).values()
    );
    exportToExcel(deduped.map(e => ({ 'Emp ID': e.empId, 'Name': e.employeeName, 'Department': e.department, 'Location': e.location, 'Vehicle': e.assignedVehicle, 'Route': e.route, 'Trips': e.totalTrips, 'Morning': e.morningTrips, 'Evening': e.eveningTrips, 'Mismatches': e.mismatchCount, 'Last Scan': e.lastScanDate })), 'Employee_Travel');
  };
  const exportScanLogs = () => { if (!scanLogs.length) return; exportToExcel(scanLogs.map(s => ({ 'Emp ID': s.empId, 'Name': s.employeeName, 'Department': s.department, 'Assigned': s.assignedVehicle, 'Traveling': s.travelingVehicle, 'Match': s.vehicleMatch ? 'Yes' : 'No', 'Route': s.route, 'Timing': s.timing, 'Scan Time': s.scanTime, 'Scanned By': s.scannedBy })), 'Scan_Logs'); };
  const exportNoShow = () => { if (!noShowData?.noShowRecords) return; exportToExcel(noShowData.noShowRecords.map(n => ({ 'Emp ID': n.empId, 'Name': n.employeeName, 'Department': n.department, 'Location': n.location, 'Vehicle': n.assignedVehicle, 'Route': n.assignedRoute, 'Days Since Scan': n.daysSinceLastScan, 'Last Scan': n.lastScanDate })), 'No_Show_Report'); };
  const exportAttendance = () => { if (!attendanceData?.attendanceRecords) return; exportToExcel(attendanceData.attendanceRecords.map(a => ({ 'Emp ID': a.empId, 'Name': a.employeeName, 'Department': a.department, 'Location': a.location, 'Route': a.route, 'Days Scanned': a.daysScanned, 'Working Days': a.workingDays, 'Compliance %': a.complianceRate, 'Morning': a.morningScans, 'Evening': a.eveningScans, 'Status': a.status })), 'Attendance_Report'); };
  const exportVehicleUtil = () => { if (!vehicleUtilData) return; exportToExcel(vehicleUtilData.map(v => ({ 'Vehicle': v.registrationNumber, 'Type': v.vehicleType, 'Location': v.location, 'Seats': v.totalSeats, 'Avg Daily Passengers': v.avgDailyPassengers, 'Max Daily': v.maxDailyPassengers, 'Utilization %': v.seatUtilizationPercent, 'Morning': v.morningScans, 'Evening': v.eveningScans, 'Unique Employees': v.uniqueEmployees })), 'Vehicle_Utilization'); };
  const exportGateman = () => { if (!gatemanData) return; exportToExcel(gatemanData.map(g => ({ 'Gateman': g.gatemanName, 'Phone': g.phone, 'Location': g.location, 'Total Scans': g.totalScans, 'Days Active': g.daysActive, 'Avg/Day': g.avgScansPerDay, 'Vehicles': g.vehiclesHandled, 'Routes': g.routesHandled })), 'Gateman_Performance'); };

  // Chart helpers
  const deptChartData = reportData?.departmentStats ? { labels: reportData.departmentStats.slice(0, 10).map(d => d.department), datasets: [{ label: 'Morning', data: reportData.departmentStats.slice(0, 10).map(d => d.morningScans), backgroundColor: 'rgba(251, 191, 36, 0.7)' }, { label: 'Evening', data: reportData.departmentStats.slice(0, 10).map(d => d.eveningScans), backgroundColor: 'rgba(59, 130, 246, 0.7)' }] } : null;
  const deptPieData = reportData?.departmentStats ? { labels: reportData.departmentStats.slice(0, 8).map(d => d.department), datasets: [{ data: reportData.departmentStats.slice(0, 8).map(d => d.totalScans), backgroundColor: ['rgba(99,102,241,0.7)', 'rgba(236,72,153,0.7)', 'rgba(34,197,94,0.7)', 'rgba(251,146,60,0.7)', 'rgba(14,165,233,0.7)', 'rgba(168,85,247,0.7)', 'rgba(244,63,94,0.7)', 'rgba(20,184,166,0.7)'], borderWidth: 0 }] } : null;
  const dailyTrendData = reportData?.dailyTrend ? { labels: reportData.dailyTrend.map(d => { const dt = new Date(d.date); return dt.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric' }); }), datasets: [{ label: 'Daily Scans', data: reportData.dailyTrend.map(d => d.scanCount), fill: true, backgroundColor: 'rgba(99,102,241,0.15)', borderColor: 'rgba(99,102,241,1)', tension: 0.4, pointRadius: 2 }] } : null;
  const routeChartData = reportData?.routeEmployeeStats ? { labels: reportData.routeEmployeeStats.slice(0, 10).map(r => r.route), datasets: [{ label: 'Unique Employees', data: reportData.routeEmployeeStats.slice(0, 10).map(r => r.uniqueEmployees), backgroundColor: 'rgba(34,197,94,0.7)' }, { label: 'Total Scans', data: reportData.routeEmployeeStats.slice(0, 10).map(r => r.totalScans), backgroundColor: 'rgba(99,102,241,0.7)' }] } : null;
  const timingPieData = reportData?.timingBreakdown ? { labels: Object.keys(reportData.timingBreakdown), datasets: [{ data: Object.values(reportData.timingBreakdown), backgroundColor: ['rgba(251,191,36,0.8)', 'rgba(59,130,246,0.8)'], borderWidth: 0 }] } : null;

  const getStatusColor = (status) => {
    switch (status) { case 'EXCELLENT': return 'status-excellent'; case 'GOOD': return 'status-good'; case 'FAIR': return 'status-fair'; case 'POOR': return 'status-poor'; default: return ''; }
  };

  const getUtilBadgeClass = (pct) => pct > 100 ? 'high' : pct > 60 ? 'medium' : 'low';

  const computeUtilSuggestions = (data) => {
    if (!data || data.length === 0) return { consolidations: [], inactive: [] };
    // Build route → vehicles map
    const routeVehicleMap = {};
    data.forEach(v => {
      (v.routes || []).forEach(route => {
        if (!routeVehicleMap[route]) routeVehicleMap[route] = [];
        routeVehicleMap[route].push(v);
      });
    });
    // Consolidation: 2+ vehicles on same route, average utilization ≤55%
    // Using average (not every) so one vehicle slightly above 50% doesn't block the suggestion
    const consolidations = Object.entries(routeVehicleMap)
      .filter(([, vehicles]) => {
        if (vehicles.length < 2) return false;
        const avg = vehicles.reduce((s, v) => s + (v.seatUtilizationPercent || 0), 0) / vehicles.length;
        return avg <= 55;
      })
      .map(([route, vehicles]) => ({
        route,
        vehicles,
        avgUtil: Math.round(vehicles.reduce((s, v) => s + (v.seatUtilizationPercent || 0), 0) / vehicles.length)
      }));
    // Inactive: zero scans or zero unique employees in selected period
    const inactive = data.filter(v =>
      v.uniqueEmployees === 0 || (v.morningScans === 0 && v.eveningScans === 0)
    );
    return { consolidations, inactive };
  };

  if (loading && !reportData) return <div className="scan-reports-container"><div className="loading-spinner">Loading reports...</div></div>;

  return (
    <div className="scan-reports-container">
      {/* Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact"><MdAssessment className="title-icon" /> Vehicle Reports & Analytics</h2>
        </div>
        <div className="header-filters-compact">
          <input type="date" className="filter-input-compact" value={filters.startDate} onChange={e => setFilters({ ...filters, startDate: e.target.value })} />
          <input type="date" className="filter-input-compact" value={filters.endDate} onChange={e => setFilters({ ...filters, endDate: e.target.value })} />
          {locations.length > 0 && (
            <select className="filter-select-compact" value={filters.location} onChange={e => setFilters({ ...filters, location: e.target.value })}>
              <option value="">All Locations</option>
              {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
            </select>
          )}
          <button className="btn-book-compact" onClick={handleFilterApply} disabled={loading}><MdRefresh /> <span>{loading ? 'Loading...' : 'Apply'}</span></button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Tab Navigation */}
      <div className="report-tabs">
        <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => handleTabChange('overview')}><MdAssessment /> Overview</button>
        <button className={`tab-btn ${activeTab === 'department' ? 'active' : ''}`} onClick={() => handleTabChange('department')}><MdPeople /> Department</button>
        <button className={`tab-btn ${activeTab === 'employee' ? 'active' : ''}`} onClick={() => handleTabChange('employee')}><MdPeople /> Employees</button>
        <button className={`tab-btn ${activeTab === 'route' ? 'active' : ''}`} onClick={() => handleTabChange('route')}><MdRoute /> Routes</button>
        <button className={`tab-btn ${activeTab === 'attendance' ? 'active' : ''}`} onClick={() => handleTabChange('attendance')}><MdFactCheck /> Attendance</button>
        <button className={`tab-btn ${activeTab === 'noShow' ? 'active' : ''}`} onClick={() => handleTabChange('noShow')}><MdEventBusy /> No-Show</button>
        <button className={`tab-btn ${activeTab === 'vehicleUtil' ? 'active' : ''}`} onClick={() => handleTabChange('vehicleUtil')}><MdDirectionsCar /> Vehicle Usage</button>
        <button className={`tab-btn ${activeTab === 'gateman' ? 'active' : ''}`} onClick={() => handleTabChange('gateman')}><MdSecurity /> Gateman</button>
        <button className={`tab-btn ${activeTab === 'monthCompare' ? 'active' : ''}`} onClick={() => handleTabChange('monthCompare')}><MdCompareArrows /> Month Compare</button>
        <button className={`tab-btn ${activeTab === 'scanLogs' ? 'active' : ''}`} onClick={() => handleTabChange('scanLogs')}><MdAssessment /> Scan Logs</button>
        <button className={`tab-btn ${activeTab === 'locationSummary' ? 'active' : ''}`} onClick={() => handleTabChange('locationSummary')}><MdAssessment /> Locations</button>
      </div>

      {tabLoading && <div className="loading-spinner">Loading data...</div>}

      {/* ═══ OVERVIEW TAB ═══ */}
      {activeTab === 'overview' && reportData && (
        <div className="tab-content">
          <div className="metrics-grid">
            <div className="metric-card"><h3>Total Scans</h3><p className="metric-value">{reportData.totalScans || 0}</p><div className="metric-details"><span>{filters.startDate} to {filters.endDate}</span></div></div>
            <div className="metric-card"><h3>Unique Employees</h3><p className="metric-value">{reportData.uniqueEmployees || 0}</p></div>
            <div className="metric-card"><h3>Vehicle Mismatches</h3><p className="metric-value">{reportData.totalMismatches || 0}</p><div className="metric-details"><span className={reportData.mismatchPercentage > 10 ? 'highlight-red' : ''}>{reportData.mismatchPercentage}%</span></div></div>
            <div className="metric-card"><h3>Departments</h3><p className="metric-value">{reportData.departmentStats?.length || 0}</p></div>
          </div>
          <div className="charts-grid">
            {dailyTrendData && <div className="chart-card wide"><h3>Daily Scan Trend</h3><Line data={dailyTrendData} options={{ responsive: true, maintainAspectRatio: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } }} /></div>}
            {deptPieData && <div className="chart-card"><h3>Scans by Department</h3><Doughnut data={deptPieData} options={{ responsive: true, plugins: { legend: { position: 'right', labels: { boxWidth: 12, padding: 8 } } } }} /></div>}
            {timingPieData && <div className="chart-card small"><h3>Morning vs Evening</h3><Doughnut data={timingPieData} options={{ responsive: true }} /></div>}
          </div>
          {reportData.routeEmployeeStats?.length > 0 && (
            <div className="route-details-section">
              <h2>Top Routes</h2>
              <div className="table-responsive"><table className="route-table"><thead><tr><th>Route</th><th>Employees</th><th>Scans</th><th>Morning</th><th>Evening</th><th>Avg Daily</th></tr></thead>
              <tbody>{reportData.routeEmployeeStats.slice(0, 10).map((r, i) => (<tr key={i}><td><strong>{r.route}</strong></td><td>{r.uniqueEmployees}</td><td>{r.totalScans}</td><td><span className="timing-badge morning">{r.morningScans}</span></td><td><span className="timing-badge evening">{r.eveningScans}</span></td><td>{r.avgDailyScans}</td></tr>))}</tbody></table></div>
            </div>
          )}
        </div>
      )}

      {/* ═══ DEPARTMENT TAB ═══ */}
      {activeTab === 'department' && reportData && (
        <div className="tab-content">
          <div className="section-header"><h2>Department-wise Analysis</h2><button className="export-btn" onClick={exportDepartmentReport}><MdFileDownload /> Export</button></div>
          {deptChartData && <div className="charts-grid"><div className="chart-card wide"><h3>Department Scans — Morning vs Evening</h3><Bar data={deptChartData} options={{ responsive: true, scales: { x: { ticks: { maxRotation: 45, minRotation: 45 } } } }} /></div>{deptPieData && <div className="chart-card"><h3>Department Share</h3><Doughnut data={deptPieData} options={{ responsive: true, plugins: { legend: { position: 'right', labels: { boxWidth: 12 } } } }} /></div>}</div>}
          {reportData.departmentStats?.length > 0 && (
            <div className="table-responsive"><table className="route-table"><thead><tr>
              <th className="sortable" onClick={() => handleDeptSort('department')}>Department {getSortIcon(deptSort, 'department')}</th>
              <th className="sortable" onClick={() => handleDeptSort('employeeCount')}>Employees {getSortIcon(deptSort, 'employeeCount')}</th>
              <th className="sortable" onClick={() => handleDeptSort('totalScans')}>Scans {getSortIcon(deptSort, 'totalScans')}</th>
              <th>Morning</th><th>Evening</th>
              <th className="sortable" onClick={() => handleDeptSort('avgScansPerEmployee')}>Avg/Emp {getSortIcon(deptSort, 'avgScansPerEmployee')}</th>
              <th className="sortable" onClick={() => handleDeptSort('percentage')}>Share {getSortIcon(deptSort, 'percentage')}</th>
            </tr></thead><tbody>
              {sortData(reportData.departmentStats, deptSort).map((d, i) => (<tr key={i}><td><strong>{d.department}</strong></td><td>{d.employeeCount}</td><td>{d.totalScans}</td><td><span className="timing-badge morning">{d.morningScans}</span></td><td><span className="timing-badge evening">{d.eveningScans}</span></td><td>{d.avgScansPerEmployee}</td><td><div className="percentage-bar"><div className="percentage-fill" style={{ width: `${Math.min(d.percentage, 100)}%` }}></div><span>{d.percentage}%</span></div></td></tr>))}
            </tbody></table></div>
          )}
        </div>
      )}

      {/* ═══ EMPLOYEE TAB ═══ */}
      {activeTab === 'employee' && reportData && (
        <div className="tab-content">
          <div className="section-header"><h2>Employee Travel Summary</h2><button className="export-btn" onClick={exportEmployeeReport}><MdFileDownload /> Export</button></div>
          <div className="inline-filters"><input type="text" placeholder="Search by ID, Name, or Department..." className="search-input" onChange={e => setFilters(f => ({ ...f, empSearchTerm: e.target.value.toLowerCase() }))} /></div>
          <div className="table-responsive"><table className="route-table"><thead><tr>
            <th className="sortable" onClick={() => handleEmpSort('empId')}>Emp ID {getSortIcon(empSort, 'empId')}</th>
            <th className="sortable" onClick={() => handleEmpSort('employeeName')}>Name {getSortIcon(empSort, 'employeeName')}</th>
            <th className="sortable" onClick={() => handleEmpSort('department')}>Department {getSortIcon(empSort, 'department')}</th>
            <th>Location</th><th>Vehicle</th><th>Route</th>
            <th className="sortable" onClick={() => handleEmpSort('totalTrips')}>Trips {getSortIcon(empSort, 'totalTrips')}</th>
            <th>AM</th><th>PM</th>
            <th className="sortable" onClick={() => handleEmpSort('mismatchCount')}>Mismatch {getSortIcon(empSort, 'mismatchCount')}</th>
            <th>Last Scan</th>
          </tr></thead><tbody>
            {sortData(
                Array.from(
                  (reportData.employeeTravelSummary || []).reduce((map, e) => {
                    const key = e.empId || `${e.employeeName}|${e.route}`;
                    if (!map.has(key)) { map.set(key, e); }
                    else {
                      const prev = map.get(key);
                      map.set(key, { ...prev, totalTrips: (prev.totalTrips || 0) + (e.totalTrips || 0), morningTrips: (prev.morningTrips || 0) + (e.morningTrips || 0), eveningTrips: (prev.eveningTrips || 0) + (e.eveningTrips || 0), mismatchCount: (prev.mismatchCount || 0) + (e.mismatchCount || 0), lastScanDate: (prev.lastScanDate || '') > (e.lastScanDate || '') ? prev.lastScanDate : e.lastScanDate });
                    }
                    return map;
                  }, new Map()).values()
                ), empSort)
              .filter(e => { const t = filters.empSearchTerm || ''; if (!t) return true; return e.empId?.toLowerCase().includes(t) || e.employeeName?.toLowerCase().includes(t) || e.department?.toLowerCase().includes(t); })
              .map((e, i) => (<tr key={i}><td>{e.empId}</td><td><strong>{e.employeeName}</strong></td><td>{e.department}</td><td>{e.location}</td><td>{e.assignedVehicle}</td><td>{e.route}</td><td>{e.totalTrips}</td><td><span className="timing-badge morning">{e.morningTrips}</span></td><td><span className="timing-badge evening">{e.eveningTrips}</span></td><td>{e.mismatchCount > 0 ? <span className="mismatch-badge">{e.mismatchCount}</span> : <span className="match-badge">0</span>}</td><td>{e.lastScanDate}</td></tr>))}
          </tbody></table></div>
        </div>
      )}

      {/* ═══ ROUTE TAB ═══ */}
      {activeTab === 'route' && reportData && (
        <div className="tab-content">
          <div className="section-header"><h2>Route-wise Analysis</h2></div>
          {routeChartData && <div className="charts-grid"><div className="chart-card wide"><h3>Route Usage — Employees vs Scans</h3><Bar data={routeChartData} options={{ responsive: true, scales: { x: { ticks: { maxRotation: 45, minRotation: 45 } } } }} /></div></div>}
          <div className="table-responsive"><table className="route-table"><thead><tr><th>Route</th><th>Employees</th><th>Scans</th><th>Morning</th><th>Evening</th><th>Avg Daily</th></tr></thead><tbody>
            {reportData.routeEmployeeStats?.map((r, i) => (<tr key={i}><td><strong>{r.route}</strong></td><td>{r.uniqueEmployees}</td><td>{r.totalScans}</td><td><span className="timing-badge morning">{r.morningScans}</span></td><td><span className="timing-badge evening">{r.eveningScans}</span></td><td>{r.avgDailyScans}</td></tr>))}
          </tbody></table></div>
        </div>
      )}

      {/* ═══ ATTENDANCE TAB ═══ */}
      {activeTab === 'attendance' && (
        <div className="tab-content">
          <div className="section-header"><h2>Attendance & Compliance</h2><button className="export-btn" onClick={exportAttendance}><MdFileDownload /> Export</button></div>
          {attendanceData && (
            <>
              <div className="metrics-grid">
                <div className="metric-card"><h3>Working Days</h3><p className="metric-value">{attendanceData.totalWorkingDays}</p></div>
                <div className="metric-card"><h3>Compliance Rate</h3><p className="metric-value">{attendanceData.overallComplianceRate}%</p></div>
                <div className="metric-card status-card excellent"><h3>Excellent (90%+)</h3><p className="metric-value">{attendanceData.excellent}</p></div>
                <div className="metric-card status-card good"><h3>Good (75-90%)</h3><p className="metric-value">{attendanceData.good}</p></div>
                <div className="metric-card status-card fair"><h3>Fair (50-75%)</h3><p className="metric-value">{attendanceData.fair}</p></div>
                <div className="metric-card status-card poor"><h3>Poor (&lt;50%)</h3><p className="metric-value">{attendanceData.poor}</p></div>
              </div>
              {attendanceData.attendanceRecords?.length > 0 && (
                <div className="table-responsive"><table className="route-table"><thead><tr>
                  <th className="sortable" onClick={() => handleAttSort('empId')}>Emp ID {getSortIcon(attSort, 'empId')}</th>
                  <th className="sortable" onClick={() => handleAttSort('employeeName')}>Name {getSortIcon(attSort, 'employeeName')}</th>
                  <th className="sortable" onClick={() => handleAttSort('department')}>Department {getSortIcon(attSort, 'department')}</th>
                  <th>Location</th><th>Route</th>
                  <th className="sortable" onClick={() => handleAttSort('daysScanned')}>Days {getSortIcon(attSort, 'daysScanned')}</th>
                  <th>Working</th>
                  <th className="sortable" onClick={() => handleAttSort('complianceRate')}>Compliance {getSortIcon(attSort, 'complianceRate')}</th>
                  <th>AM</th><th>PM</th>
                  <th className="sortable" onClick={() => handleAttSort('status')}>Status {getSortIcon(attSort, 'status')}</th>
                </tr></thead><tbody>
                  {sortData(attendanceData.attendanceRecords, attSort).map((a, i) => (
                    <tr key={i}><td>{a.empId}</td><td><strong>{a.employeeName}</strong></td><td>{a.department}</td><td>{a.location}</td><td>{a.route}</td><td>{a.daysScanned}</td><td>{a.workingDays}</td>
                    <td><div className="percentage-bar"><div className="percentage-fill" style={{ width: `${Math.min(a.complianceRate, 100)}%`, backgroundColor: a.complianceRate >= 90 ? '#10b981' : a.complianceRate >= 75 ? '#3b82f6' : a.complianceRate >= 50 ? '#f59e0b' : '#ef4444' }}></div><span>{a.complianceRate}%</span></div></td>
                    <td><span className="timing-badge morning">{a.morningScans}</span></td><td><span className="timing-badge evening">{a.eveningScans}</span></td>
                    <td><span className={`status-badge-pill ${getStatusColor(a.status)}`}>{a.status}</span></td></tr>
                  ))}
                </tbody></table></div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══ NO-SHOW TAB ═══ */}
      {activeTab === 'noShow' && (
        <div className="tab-content">
          <div className="section-header"><h2>No-Show Report</h2><button className="export-btn" onClick={exportNoShow}><MdFileDownload /> Export</button></div>
          {noShowData && (
            <>
              <div className="metrics-grid small-grid">
                <div className="metric-card"><h3>Assigned Employees</h3><p className="metric-value">{noShowData.totalAssignedEmployees}</p></div>
                <div className="metric-card warning-card"><h3>No-Shows</h3><p className="metric-value">{noShowData.totalNoShows}</p></div>
                <div className="metric-card"><h3>No-Show Rate</h3><p className="metric-value">{noShowData.noShowPercentage}%</p></div>
              </div>
              {noShowData.noShowRecords?.length > 0 ? (
                <div className="table-responsive"><table className="route-table"><thead><tr><th>Emp ID</th><th>Name</th><th>Department</th><th>Location</th><th>Vehicle</th><th>Route</th><th>Days Since Scan</th><th>Last Scan</th></tr></thead><tbody>
                  {noShowData.noShowRecords.map((n, i) => (
                    <tr key={i} className="mismatch-row"><td>{n.empId}</td><td><strong>{n.employeeName}</strong></td><td>{n.department}</td><td>{n.location}</td><td>{n.assignedVehicle}</td><td>{n.assignedRoute}</td>
                    <td><span className={n.daysSinceLastScan > 30 ? 'mismatch-badge' : n.daysSinceLastScan > 7 ? 'timing-badge morning' : 'match-badge'}>{n.daysSinceLastScan >= 0 ? n.daysSinceLastScan : 'Never'}</span></td>
                    <td>{n.lastScanDate}</td></tr>
                  ))}
                </tbody></table></div>
              ) : <div className="no-data-message">All assigned employees were scanned in this period.</div>}
            </>
          )}
        </div>
      )}

      {/* ═══ VEHICLE UTILIZATION TAB ═══ */}
      {activeTab === 'vehicleUtil' && (
        <div className="tab-content">
          <div className="section-header"><h2>Vehicle Actual Utilization (from Scans)</h2><button className="export-btn" onClick={exportVehicleUtil}><MdFileDownload /> Export</button></div>
          {vehicleUtilData && vehicleUtilData.length > 0 && (
            <>
              {/* ── Smart Suggestions ── */}
              {(() => {
                const { consolidations, inactive } = computeUtilSuggestions(vehicleUtilData);
                if (!consolidations.length && !inactive.length) return null;
                return (
                  <div className="util-suggestions">
                    {/* Collapsed header — always visible */}
                    <div className="suggestions-header" onClick={() => setSuggestionsOpen(o => !o)}>
                      <span className="suggestions-title">💡 Smart Suggestions</span>
                      <div className="suggestions-pills">
                        {consolidations.length > 0 && (
                          <span className="suggestions-pill blue">🔀 {consolidations.length} consolidation{consolidations.length !== 1 ? 's' : ''}</span>
                        )}
                        {inactive.length > 0 && (
                          <span className="suggestions-pill orange">⚠️ {inactive.length} inactive vehicle{inactive.length !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                      <span className="suggestions-toggle">{suggestionsOpen ? '▲ Hide' : '▼ Show'}</span>
                    </div>

                    {/* Expanded body */}
                    {suggestionsOpen && (
                      <div className="suggestions-body">
                        {/* Consolidation rows — one per route */}
                        {consolidations.map((s, i) => (
                          <div key={`con-${i}`} className="suggestion-row consolidation">
                            <span className="suggestion-row-icon">🔀</span>
                            <div className="suggestion-row-content">
                              <span>Route <strong>{s.route}</strong> — {s.vehicles.length} vehicles at avg {s.avgUtil}% seat utilization. Consider consolidating to fewer vehicles to reduce cost.</span>
                              <div className="suggestion-chips">
                                {s.vehicles.map(v => (
                                  <span key={v.registrationNumber} className="chip chip-blue">{v.registrationNumber} ({v.seatUtilizationPercent}%)</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* All inactive vehicles in ONE grouped row */}
                        {inactive.length > 0 && (
                          <div className="suggestion-row removal">
                            <span className="suggestion-row-icon">⚠️</span>
                            <div className="suggestion-row-content">
                              <span><strong>{inactive.length} vehicle{inactive.length !== 1 ? 's' : ''}</strong> had no activity in the selected period — consider removing or reassigning them.</span>
                              <div className="suggestion-chips scrollable-chips">
                                {inactive.map(v => (
                                  <span key={v.registrationNumber} className="chip chip-orange" title={v.vehicleType}>{v.registrationNumber}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="charts-grid">
                <div className="chart-card wide">
                  <h3>Seat Utilization by Vehicle</h3>
                  <Bar data={{
                    labels: vehicleUtilData.slice(0, 15).map(v => v.registrationNumber),
                    datasets: [
                      { label: 'Avg Daily Passengers', data: vehicleUtilData.slice(0, 15).map(v => v.avgDailyPassengers), backgroundColor: 'rgba(99,102,241,0.7)' },
                      { label: 'Total Seats', data: vehicleUtilData.slice(0, 15).map(v => v.totalSeats), backgroundColor: 'rgba(229,231,235,0.7)' }
                    ]
                  }} options={{ responsive: true, scales: { x: { ticks: { maxRotation: 45, minRotation: 45 } } } }} />
                </div>
              </div>
              <div className="table-responsive"><table className="route-table"><thead><tr><th>Vehicle</th><th>Type</th><th>Location</th><th>Seats</th><th>Avg Daily</th><th>Max Daily</th><th>Utilization %</th><th>Morning</th><th>Evening</th><th>Unique Emps</th><th>Routes</th></tr></thead><tbody>
                {vehicleUtilData.map((v, i) => (
                  <tr key={i}><td><strong>{v.registrationNumber}</strong></td><td>{v.vehicleType}</td><td>{v.location}</td><td>{v.totalSeats}</td><td>{v.avgDailyPassengers}</td><td>{v.maxDailyPassengers}</td>
                  <td><span className={`utilization-badge ${getUtilBadgeClass(v.seatUtilizationPercent)}`}>{v.seatUtilizationPercent}%</span></td>
                  <td><span className="timing-badge morning">{v.morningScans}</span></td><td><span className="timing-badge evening">{v.eveningScans}</span></td><td>{v.uniqueEmployees}</td><td>{v.routes?.join(', ')}</td></tr>
                ))}
              </tbody></table></div>
            </>
          )}
          {vehicleUtilData && vehicleUtilData.length === 0 && <div className="no-data-message">No scan data found for vehicles in this period.</div>}
        </div>
      )}

      {/* ═══ GATEMAN TAB ═══ */}
      {activeTab === 'gateman' && (
        <div className="tab-content">
          <div className="section-header"><h2>Gateman Performance</h2><button className="export-btn" onClick={exportGateman}><MdFileDownload /> Export</button></div>
          {gatemanData && gatemanData.length > 0 && (
            <>
              <div className="charts-grid">
                <div className="chart-card wide">
                  <h3>Scans by Gateman</h3>
                  <Bar data={{
                    labels: gatemanData.map(g => g.gatemanName),
                    datasets: [{ label: 'Total Scans', data: gatemanData.map(g => g.totalScans), backgroundColor: 'rgba(16,185,129,0.7)' }]
                  }} options={{ responsive: true, indexAxis: 'y' }} />
                </div>
              </div>
              <div className="table-responsive"><table className="route-table"><thead><tr><th>Gateman</th><th>Phone</th><th>Location</th><th>Total Scans</th><th>Days Active</th><th>Avg/Day</th><th>Vehicles</th><th>Routes</th></tr></thead><tbody>
                {gatemanData.map((g, i) => (
                  <tr key={i}><td><strong>{g.gatemanName}</strong></td><td>{g.phone}</td><td>{g.location}</td><td>{g.totalScans}</td><td>{g.daysActive}</td><td>{g.avgScansPerDay}</td><td>{g.vehiclesHandled}</td><td>{g.routesHandled}</td></tr>
                ))}
              </tbody></table></div>
            </>
          )}
          {gatemanData && gatemanData.length === 0 && <div className="no-data-message">No gateman scan data found.</div>}
        </div>
      )}

      {/* ═══ MONTH COMPARISON TAB ═══ */}
      {activeTab === 'monthCompare' && (
        <div className="tab-content">
          <div className="section-header"><h2>Month-over-Month Comparison</h2></div>
          {monthData && (
            <>
              <div className="month-compare-grid">
                <div className="month-card">
                  <h3>{monthData.previousMonth?.monthLabel || 'Previous Month'}</h3>
                  <div className="month-stats">
                    <div><span className="stat-label">Scans</span><span className="stat-value">{monthData.previousMonth?.totalScans || 0}</span></div>
                    <div><span className="stat-label">Employees</span><span className="stat-value">{monthData.previousMonth?.uniqueEmployees || 0}</span></div>
                    <div><span className="stat-label">Morning</span><span className="stat-value">{monthData.previousMonth?.morningScans || 0}</span></div>
                    <div><span className="stat-label">Evening</span><span className="stat-value">{monthData.previousMonth?.eveningScans || 0}</span></div>
                    <div><span className="stat-label">Avg Daily</span><span className="stat-value">{monthData.previousMonth?.avgDailyScans || 0}</span></div>
                  </div>
                </div>
                <div className="month-arrow">
                  <div className={`growth-badge ${monthData.scanGrowthPercent >= 0 ? 'positive' : 'negative'}`}>
                    {monthData.scanGrowthPercent >= 0 ? '+' : ''}{monthData.scanGrowthPercent}%
                  </div>
                  <span>Scan Growth</span>
                </div>
                <div className="month-card current">
                  <h3>{monthData.currentMonth?.monthLabel || 'Current Month'}</h3>
                  <div className="month-stats">
                    <div><span className="stat-label">Scans</span><span className="stat-value">{monthData.currentMonth?.totalScans || 0}</span></div>
                    <div><span className="stat-label">Employees</span><span className="stat-value">{monthData.currentMonth?.uniqueEmployees || 0}</span></div>
                    <div><span className="stat-label">Morning</span><span className="stat-value">{monthData.currentMonth?.morningScans || 0}</span></div>
                    <div><span className="stat-label">Evening</span><span className="stat-value">{monthData.currentMonth?.eveningScans || 0}</span></div>
                    <div><span className="stat-label">Avg Daily</span><span className="stat-value">{monthData.currentMonth?.avgDailyScans || 0}</span></div>
                  </div>
                </div>
              </div>

              <div className="metrics-grid small-grid" style={{ marginTop: '24px' }}>
                <div className="metric-card"><h3>New Employees</h3><p className="metric-value" style={{ color: '#10b981' }}>{monthData.newEmployees || 0}</p></div>
                <div className="metric-card"><h3>Dropped Employees</h3><p className="metric-value" style={{ color: '#ef4444' }}>{monthData.droppedEmployees || 0}</p></div>
                <div className="metric-card"><h3>Employee Growth</h3><p className="metric-value" style={{ color: monthData.employeeGrowthPercent >= 0 ? '#10b981' : '#ef4444' }}>{monthData.employeeGrowthPercent >= 0 ? '+' : ''}{monthData.employeeGrowthPercent}%</p></div>
              </div>

              {monthData.departmentChanges && Object.keys(monthData.departmentChanges).length > 0 && (
                <div className="route-details-section" style={{ marginTop: '24px' }}>
                  <h2>Department Changes</h2>
                  <div className="table-responsive"><table className="route-table"><thead><tr><th>Department</th><th>Previous Month</th><th>Current Month</th><th>Change</th></tr></thead><tbody>
                    {Object.entries(monthData.departmentChanges).sort((a, b) => Math.abs(b[1].changePercent) - Math.abs(a[1].changePercent)).map(([dept, data], i) => (
                      <tr key={i}><td><strong>{dept}</strong></td><td>{data.previousScans}</td><td>{data.currentScans}</td>
                      <td><span className={`growth-badge-inline ${data.changePercent >= 0 ? 'positive' : 'negative'}`}>{data.changePercent >= 0 ? '+' : ''}{data.changePercent}%</span></td></tr>
                    ))}
                  </tbody></table></div>
                </div>
              )}
            </>
          )}
        </div>
      )}


      {/* ═══ SCAN LOGS TAB ═══ */}
      {activeTab === 'scanLogs' && (
        <div className="tab-content">
          <div className="section-header"><h2>Detailed Scan Logs</h2><button className="export-btn" onClick={exportScanLogs} disabled={!scanLogs.length}><MdFileDownload /> Export</button></div>
          <div className="inline-filters">
            <select className="filter-select-compact" value={filters.timing} onChange={e => setFilters({ ...filters, timing: e.target.value })}><option value="">All Timings</option><option value="morning">Morning</option><option value="evening">Evening</option></select>
            <input type="text" placeholder="Employee ID" className="filter-input-compact" value={filters.empId} onChange={e => setFilters({ ...filters, empId: e.target.value })} />
            <input type="text" placeholder="Vehicle" className="filter-input-compact" value={filters.vehicle} onChange={e => setFilters({ ...filters, vehicle: e.target.value })} />
            <input type="text" placeholder="Route" className="filter-input-compact" value={filters.route} onChange={e => setFilters({ ...filters, route: e.target.value })} />
            <input type="text" placeholder="Scanned By" className="filter-input-compact" value={filters.scannedBy} onChange={e => setFilters({ ...filters, scannedBy: e.target.value })} />
            <button className="btn-book-compact" onClick={() => searchScanLogs(filters)}><MdRefresh /> Search</button>
          </div>
          {scanLogs.length > 0 ? (
            <><div className="results-count">{scanLogs.length} records</div>
            <div className="table-responsive"><table className="route-table"><thead><tr><th>Emp ID</th><th>Name</th><th>Department</th><th>Assigned</th><th>Traveling</th><th>Match</th><th>Route</th><th>Timing</th><th>Scan Time</th><th>Scanned By</th></tr></thead><tbody>
              {scanLogs.map((s, i) => (<tr key={i} className={!s.vehicleMatch ? 'mismatch-row' : ''}><td>{s.empId}</td><td><strong>{s.employeeName}</strong></td><td>{s.department}</td><td>{s.assignedVehicle}</td><td>{s.travelingVehicle}</td><td>{s.vehicleMatch ? <span className="match-badge">Yes</span> : <span className="mismatch-badge">No</span>}</td><td>{s.route}</td><td><span className={`timing-badge ${s.timing?.toLowerCase()}`}>{s.timing}</span></td><td>{s.scanTime}</td><td>{s.scannedBy}</td></tr>))}
            </tbody></table></div></>
          ) : <div className="no-data-message">No scan logs found. Apply filters and click Search.</div>}
        </div>
      )}

      {/* ═══ LOCATION SUMMARY TAB ═══ */}
      {activeTab === 'locationSummary' && (
        <div className="tab-content">
          <div className="section-header"><h2>Location-wise Summary</h2><button className="export-btn" onClick={() => { if (locationSummaryData?.locations) exportToExcel(locationSummaryData.locations.map(l => ({ Location: l.location, 'Total Scans': l.totalScans, 'Unique Employees': l.uniqueEmployees, 'Morning': l.morningScans, 'Evening': l.eveningScans, 'Vehicles': l.vehicleCount, 'Routes': l.routeCount, 'Capacity': l.totalCapacity, 'Assigned Employees': l.assignedEmployees, 'Avg Daily Scans': l.avgDailyScans, 'Mismatches': l.mismatchCount, 'Mismatch %': l.mismatchPercentage, 'Scan Share %': l.scanShare })), 'Location_Summary'); }}><MdFileDownload /> Export</button></div>
          {tabLoading ? <div className="loading-message">Loading location data...</div> : locationSummaryData ? (
            <>
              <div className="metrics-grid small-grid">
                <div className="metric-card"><h3>Locations</h3><p className="metric-value">{locationSummaryData.totalLocations}</p></div>
                <div className="metric-card"><h3>Total Scans</h3><p className="metric-value">{locationSummaryData.grandTotalScans?.toLocaleString()}</p></div>
              </div>

              {/* Location Comparison Bar Chart */}
              {locationSummaryData.locations?.length > 1 && (
                <div className="chart-container" style={{ marginBottom: '24px' }}>
                  <Bar data={{
                    labels: locationSummaryData.locations.map(l => l.location),
                    datasets: [
                      { label: 'Morning', data: locationSummaryData.locations.map(l => l.morningScans), backgroundColor: '#4ade80' },
                      { label: 'Evening', data: locationSummaryData.locations.map(l => l.eveningScans), backgroundColor: '#60a5fa' },
                    ]
                  }} options={{ responsive: true, plugins: { title: { display: true, text: 'Scans by Location (Morning vs Evening)' } }, scales: { x: { stacked: true }, y: { stacked: true } } }} />
                </div>
              )}

              {/* Location Summary Table */}
              <div className="table-responsive"><table className="route-table"><thead><tr>
                <th>Location</th><th>Scans</th><th>Employees</th><th>Morning</th><th>Evening</th>
                <th>Vehicles</th><th>Routes</th><th>Capacity</th><th>Assigned</th><th>Avg/Day</th>
                <th>Mismatches</th><th>Mismatch %</th><th>Share %</th>
              </tr></thead><tbody>
                {locationSummaryData.locations?.map((l, i) => (
                  <tr key={i}>
                    <td><strong style={{ color: '#2563eb' }}>{l.location}</strong></td>
                    <td><strong>{l.totalScans?.toLocaleString()}</strong></td>
                    <td>{l.uniqueEmployees}</td>
                    <td>{l.morningScans?.toLocaleString()}</td><td>{l.eveningScans?.toLocaleString()}</td>
                    <td>{l.vehicleCount}</td><td>{l.routeCount}</td><td>{l.totalCapacity}</td><td>{l.assignedEmployees}</td>
                    <td>{l.avgDailyScans}</td>
                    <td>{l.mismatchCount}</td><td>{l.mismatchPercentage}%</td>
                    <td><span className="scan-share-badge">{l.scanShare}%</span></td>
                  </tr>
                ))}
              </tbody></table></div>

              {/* Department breakdown per location (collapsed) */}
              {locationSummaryData.locations?.map((l, li) => (
                l.departments?.length > 0 && (
                  <div key={li} style={{ marginTop: '16px' }}>
                    <h3 style={{ fontSize: '14px', color: '#374151', marginBottom: '8px', cursor: 'pointer' }}
                        onClick={() => setExpandedDept(expandedDept === l.location ? null : l.location)}>
                      {expandedDept === l.location ? '▼' : '▶'} {l.location} — Department Breakdown ({l.departments.length})
                    </h3>
                    {expandedDept === l.location && (
                      <div className="table-responsive"><table className="route-table"><thead><tr>
                        <th>Department</th><th>Employees</th><th>Scans</th><th>Morning</th><th>Evening</th><th>Avg/Emp</th>
                      </tr></thead><tbody>
                        {l.departments.map((d, di) => (
                          <tr key={di}><td>{d.department}</td><td>{d.employeeCount}</td><td>{d.totalScans}</td><td>{d.morningScans}</td><td>{d.eveningScans}</td><td>{d.avgScansPerEmployee}</td></tr>
                        ))}
                      </tbody></table></div>
                    )}
                  </div>
                )
              ))}
            </>
          ) : <div className="no-data-message">No location data available.</div>}
        </div>
      )}

      {/* ═══ LOCATION → DEPARTMENT DRILL-DOWN TAB ═══ */}

    </div>
  );
}

export default VehicleScanReports;
